<template>
  <div class="meeting-navigator" :class="{'closed': closed}">
    <div class="header">
      <div v-if="!this.$store.state.device.isSmartphonePortrait" class="role-displayer">
        <div class="button-role" @click="itemExpand(roleExpand)">
          <span class="button-title">{{ this.$t('message.MEETING_ROLES') }}</span>
          <Icon
            v-if="!$store.state.device.isSmartphonePortrait"
            :class="{expand: roleExpand.expanded}"
            class="arrow"
          >
            {{ arrowDownThin }}
          </Icon>
        </div>
        <div v-if="!rolesData && rolesLoading" :id="roleExpand.id" class="role-container">
          <div class="no-roles">
            {{ this.$t('message.LOADING') }}
          </div>
        </div>
        <div v-else-if="userRoles.length === 0" :id="roleExpand.id" class="role-container">
          <div class="no-roles">
            {{ this.$t('message.MEETING_NO_ROLES') }}
          </div>
        </div>
        <div v-else :id="roleExpand.id" class="role-container">
          <div v-for="user in userRoles" :key="user.id" class="role-definition">
            <div v-if="user.role !== null">
              <div class="image-container">
                <Icon
                  :style="'background-color: ' + rolesDefinition[user.role - 1].color"
                  class="role-image generic-border"
                >
                  {{ rolesDefinition[user.role - 1].src }}
                </Icon>
              </div>
              <div class="role-content">
                {{ $t('fullNameFormat', user) }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <transition name="fade">
        <div
          v-if="$store.state.device.isSmartphonePortrait || closed && !isMeetingSpectator"
          class="meeting-navigator__closed-buttons"
        >
          <div
            class="left-right__img generic-border generic-border--icon-button generic-border--blue"
            @click="prev"
          >
            <Icon>{{ rewMedium }}</Icon>
          </div>
          <div
            class="left-right__img generic-border generic-border--icon-button generic-border--blue"
            @click="next"
          >
            <Icon>{{ ffMedium }}</Icon>
          </div>
          <span class="meeting-navigator__step-viewer">
            {{ currentStepNb + 1 }} / {{ meetingData.steps.length }}
          </span>
        </div>
      </transition>
      <div
        class="close-img generic-border generic-border--icon-button generic-border--blue"
        @click="close"
      >
        <Icon class="close-img--icon">{{ arrowDownThin }}</Icon>
      </div>
    </div>
    <div class="content">
      <div v-if="currentInfo != ''" class="step-info">
        {{ currentInfo }}
      </div>
      <div class="bottom-content">
        <div class="v-centerer inline f-h m-nav-buttons">
          <div
            class="left-right__img generic-border generic-border--icon-button generic-border--clickable"
            :style="navBtnStyle"
            @click="prev"
          >
            <Icon>{{ rewMedium }}</Icon>
          </div>
          <span :class="{ highlight: !followingStep }" @click="currentStepClicked">
            <EllipsisDiv class="h-v-centerer" :text="currentStep.name" />
          </span>
          <div
            class="left-right__img generic-border generic-border--icon-button generic-border--clickable"
            :style="navBtnStyle"
            @click="next"
          >
            <Icon>{{ ffMedium }}</Icon>
          </div>
          <transition name="fade">
            <div
              v-if="!following"
              v-quick-title="$t('message.MT_CURRENT_PAGE')"
              class="m-nav-cstep-bubble"
              @click="currentPageClicked"
            >
              <img
                oncontextmenu="return false;"
                class="f-w"
                src="/images/resume-white.png"
                alt=""
              >
            </div>
          </transition>
        </div>
        <div class="v-centerer inline m-nav-time" :style="currentTimeStyle">
          {{ currentTime }}
        </div>
        <div class="m-nav-slider">
          <div class="slider-wrapper v-centerer inline f-w">
            <VueSlider
              v-model="progress"
              style="margin-bottom: 0px;"
              v-bind="meetingSliderProperties"
            >
              <template v-slot:label="{ value: index }">
                <div
                  v-if="stepAtDuration[index]"
                  v-quick-title="stepAtDuration[index].name"
                  :class="sliderLabelClass(index)"
                  @click="goToStep(stepAtDuration[index].number - 1)"
                >
                  <span>{{ bubble ? stepAtDuration[index].number : stepAtDuration[index].name }}</span>
                </div>
              </template>
            </VueSlider>
            <div
              class="generic-border generic-border--icon-button generic-border--blue generic-border--clickable"
              style="margin-left: 10px;"
              @click="promptEndMeeting"
            >
              {{ $t('message.END') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Bar at the bottom of the screen to navigate while in a meeting
 */
import { mapGetters, mapState } from 'vuex';
import { isEqual, cloneDeep } from 'lodash';
import $ from 'jquery';
// Import components
import VueSlider from 'vue-slider-component';
import EllipsisDiv from '@/components/ellipsize.vue';
import Icon from '@/components/icon.vue';
import mixinMeetingPages from '@/mixins/meeting-pages.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import utils
import { calert, secondsToArray } from '@/utils/main';
import { itemExpand } from '@/utils/expandable';
import { GET_ROLES, GET_TEAMS, SET_ACTIVE_ROUTE_CLICKED } from '@/store/mutation-types';
import { FETCH_ROLES, FETCH_TEAMS } from '@/store/action-types';
import { LEAVE_MEETING } from '@/store/modules/meeting/action-types';
import { SET_MEETING_DATA } from '@/store/modules/meeting/mutation-types';
import { ROLES } from '@/utils/meeting-utils';
import { SColors } from '@/utils/constants';
import { modalEditionQueryParams } from '@/utils/navigation-constants';
import { ShizenGlobalEvent, globalEventDispatcher } from '@/utils/event-dispatcher/global';
// Assets
import { arrowDownThin, ffMedium, rewMedium } from '@/assets/icons';

export default {

  name: 'MeetingNavigator',

  components: {
    EllipsisDiv,
    Icon,
    VueSlider,
  },

  mixins: [mixinMeetingPages, mixinSocketHandler],

  data() {
    return {
      arrowDownThin,
      ffMedium,
      rewMedium,
      meetingData: null,
      roleExpand: { expanded: false, id: 'expand-role' },
      progress: 0,
      maxDuration: 0,
      /** Elapsed time since the beginning of the meeting (in seconds) */
      startDate: null,
      secondsFromStart: 0,
      /** This depnds only on whether the user clicked next / prev, not on the time */
      currentStepNb: 0,
      /** key = duration in seconds ; value = step or undefined if no step at this time */
      stepAtDuration: {},
      /** Whether we should display the entire steps names or just the numbers in bubble */
      bubble: true,
      /** navigator can be closed down */
      closed: false,
      /** The page the leader is on */
      currentPage: '',
      rolesDefinition: ROLES.map(r => ({ ...r, title: this.$t(r.titleTr), definition: this.$t(r.definitionTr) })),
    };
  },

  computed: {
    /** Object: Meeting object from state */
    stateMeetingData() {
      return this.$store.state.meeting.meetingData;
    },
    /** Boolean: Whether this device is a spectator of the meeting */
    isMeetingSpectator() {
      return this.$store.state.meeting.isMeetingSpectator;
    },
    /** Return the current step object */
    currentStep() {
      return !this.meetingData.steps.length
        ? { id: 0, name: '', page: '' }
        : this.meetingData.steps[this.currentStepNb];
    },
    roleDefinition() {
      return this.rolesDefinition;
    },
    meetingSliderProperties() {
      const props = {
        width: '100%',
        height: 4,
        dotSize: 0,
        min: 0,
        max: this.maxDuration,
        disabled: true,
        interval: 1,
        dotStyle: { backgroundColor: SColors.mainTextColor },
        tooltipStyle: { display: 'none' },
        processStyle: { backgroundColor: SColors.mainTextColor },
        marks: val => (this.stepAtDuration[val] ? ({
          label: val,
          style: { background: SColors.mainTextColor },
        }) : false),
      };
      return props;
    },
    /** Readable display of time since the beginning */
    currentTime() {
      const time = secondsToArray(this.secondsFromStart);
      return `${time[0]}:${time[1]}:${time[2]}`;
    },
    /** If the meeting is late, display red */
    currentTimeStyle() {
      const step = this.currentStep;
      if (step && step.maxDuration && this.secondsFromStart > step.maxDuration) {
        return 'color:rgb(192, 15, 21)';
      }
      return '';
    },
    /** Current step additionnal info */
    currentInfo() {
      return this.currentStep.info || '';
    },
    /** True if the user is following the steps as they come */
    followingStep() {
      return this.currentStepLink
        ? this.$route.fullPath === this.currentStepLink
        : true;
    },
    /** True if the user is on the same page as the meeting leader */
    following() {
      return this.currentPage
        ? this.$route.fullPath === this.currentPage
        : true;
    },
    /** Returns the router link to the current step */
    currentStepLink() {
      return this.getPageLink(this.currentStep);
    },
    navBtnStyle() {
      return {
        cursor: this.isMeetingSpectator ? 'not-allowed' : 'pointer',
        opacity: this.isMeetingSpectator ? 0.3 : 1,
      };
    },
    participants() {
      if (!this.teamsData) return [];
      return this.meetingData.participants.map(p => this.companyUsers[p.user]);
    },
    userRoles() {
      if (!this.teamsData || !this.rolesData) return [];
      // Trigger expander resize
      if (this.roleExpand.expanded) {
        this.$nextTick(() => {
          itemExpand({ expanded: false }, this.roleExpand.id);
        });
      }
      return this.rolesData
        .filter(u => u.role !== null)
        .map(u => Object.assign(cloneDeep(this.companyUsers[u.user]), { role: u.role }));
    },
    ...mapGetters([
      'companyUsers',
    ]),
    ...mapState({
      teamsData: state => state[GET_TEAMS.stateKey],
      rolesLoading: state => state[GET_ROLES.loadingKey],
      rolesData: state => state[GET_ROLES.stateKey],
    }),
  },

  watch: {
    stateMeetingData(newVal) {
      if (!isEqual(newVal, this.meetingData)) {
        this.setupMeetingData();
      }
    },
    $route(to) {
      if (!this.isMeetingSpectator) {
        this.currentPage = to.fullPath;
        // TODO: Vue axios
        this.App.post(
          '/server/meetings/setPage.php',
          { meeting: this.meetingData.id, page: to.fullPath },
          (d) => {
            if (d === 1) {
              this.sendSocketMessage({ msg: SocketMessages.MT_MSGS.PAGE_CHANGE, id: this.meetingData.id });
            }
          },
        );
        if (this.currentPage === '/meeting-rating' && this.roleExpand.expanded === false) {
          itemExpand(this.roleExpand, this.roleExpand.id);
        }
      }
    },
  },

  created() {
    this.registerSocketHandlers();
    this.setupMeetingData();
  },

  mounted() {
    this.start();
    if (!this.meetingData.steps.length) {
      this.maxDuration = 0;
    } else {
      $(window).on(`resizeEnd.${this.uuid}`, () => {
        if (this.roleExpand.expanded === true) {
          this.itemExpand({ expanded: false }, this.roleExpand.id);
        }
      });
      if (this.$route.fullPath === '/meeting-rating') {
        this.itemExpand(this.roleExpand, this.roleExpand.id);
      }
    }
    this.handleSocketMessage(SocketMessages.MT_MSGS.ROLES_UPDATE, () => {
      this.$store.dispatch(FETCH_ROLES, { meeting: this.meetingData.id });
    });
  },

  methods: {
    itemExpand,
    // Using a method to create a "copy" of the meeting state to add stepAtDuration and maxDuration in it.
    setupMeetingData() {
      // First, we retrieve the data from the state and we clone them to a data into the page.
      this.meetingData = cloneDeep(this.$store.state.meeting.meetingData);
      let max = 0;
      // Then, for each step in this data, we add the stepAtDuration parameter in those.
      for (let i = 0, len = this.meetingData.steps.length; i < len; i++) {
        const step = this.meetingData.steps[i];
        step.number = i + 1;
        this.stepAtDuration[`${max}`] = step;
        max += step.duration;
        this.$set(step, 'maxDuration', max);
      }
      // Finally, the maxDuration is defined by taking the sum of all the stepAtDuration above.
      this.maxDuration = max;
    },
    registerSocketHandlers() {
      this.handleSocketMessage(SocketMessages.MT_MSGS.STEP_CHANGE, (socketData) => {
        this.stepChanged(socketData);
      });
      this.handleSocketMessage(SocketMessages.MT_MSGS.PAGE_CHANGE, (socketData) => {
        this.pageChanged(socketData);
      });
    },
    close() {
      this.closed = !this.closed;
    },
    start() {
      this.startDate = this.$moment(this.meetingData.created);
      // When joining a meeting, meeting's current step may b > 0
      const mcs = Number(this.meetingData.step);
      this.currentStepNb = mcs >= 0 && mcs < this.meetingData.steps.length ? mcs : 0;
      // Timer
      setInterval(() => {
        // Display the time and move theslider progress
        this.secondsFromStart = this.$moment().diff(this.startDate, 's');
        // The progress
        this.progress = this.secondsFromStart > this.maxDuration ? this.maxDuration : this.secondsFromStart;
      }, 1000);
      this.pushNewPage(this.meetingData.page === '' ? this.currentStepLink : this.meetingData.page);
      this.$store.dispatch(FETCH_ROLES, { meeting: this.meetingData.id });
      this.$store.dispatch(FETCH_TEAMS);
    },
    goToStep(index) {
      const stepTotal = this.meetingData.steps.length;
      if (!this.isMeetingSpectator) {
        if (index < 0 || index >= stepTotal) {
          this.promptEndMeeting();
        } else {
          this.currentStepNb = index;
          this.upStep();
        }
      }
    },
    prev() {
      this.goToStep(this.currentStepNb - 1);
    },
    next() {
      this.goToStep(this.currentStepNb + 1);
    },
    upStep() {
      this.pushNewPage(this.currentStepLink);
      this.updatelocalStorage();
      this.sendSocketMessage({
        msg: SocketMessages.MT_MSGS.STEP_CHANGE,
        id: this.currentStepNb,
        meeting: this.meetingData.id,
      });
      this.App.post('/server/meetings/setStep.php', { meeting: this.meetingData.id, step: this.currentStepNb });
    },
    updatelocalStorage() {
      if (!this.isMeetingSpectator) {
        this.$store.commit(
          `meeting/${SET_MEETING_DATA}`,
          { meeting: { ...this.meetingData, step: this.currentStepNb } },
        );
        localStorage.setItem(this.$store.getters.storedMeetingKey, JSON.stringify(this.stateMeetingData));
      }
    },
    pushNewPage(link) {
      const onCompleted = () => this.$store.commit(SET_ACTIVE_ROUTE_CLICKED, this.$route);
      const routeParts = link.split('/');
      if (typeof routeParts[1] === 'undefined') {
        return;
      }
      const newPath = routeParts[1].split('?')[0];
      const query = {};
      modalEditionQueryParams.forEach((qParam) => {
        if (link.indexOf(qParam) > -1) {
          query.followingMeeting = 1;
        }
      });
      this.$router.push({ path: link, query }, onCompleted);
      this.currentPage = link;
    },
    /** Push the current step's page */
    currentStepClicked() {
      this.$router.push(this.currentStepLink);
    },
    /** Push the current page */
    currentPageClicked() {
      this.$router.push(this.currentPage);
      if (this.currentPage === '/meeting-rating' && this.currentPage === this.$route.fullPath
        && this.roleExpand.expanded === false) {
        itemExpand(this.roleExpand, this.roleExpand.id);
      }
    },
    /** Called when receiving a step change socket event */
    stepChanged(data) {
      let step = data.id;
      if (this.meetingData.id === Number(data.meeting) && !isNaN(step)) {
        step = Number(step);
        if (step < this.meetingData.steps.length && step >= 0) {
          const follow = this.following;
          this.currentStepNb = step;
          if (follow) {
            this.pushNewPage(this.currentStepLink);
          }
        }
      }
    },
    /** Called when receiving a page change socket event */
    pageChanged(data) {
      if (this.meetingData.id === Number(data.id)) {
        this.App.post('/server/meetings/getPage.php', { meeting: this.meetingData.id }, (d) => {
          if (typeof d === 'object') {
            if (this.following) {
              this.pushNewPage(d.page);
            }
            this.currentPage = d.page;
            if (this.currentPage === '/meeting-rating' && this.currentPage === this.$route.fullPath
              && this.roleExpand.expanded === false) {
              itemExpand(this.roleExpand, this.roleExpand.id);
            }
          }
        });
      }
    },
    /** Ask confirmation to the user to end the meeting */
    promptEndMeeting() {
      calert(this.$t(this.isMeetingSpectator ? 'message.MEETING_LEAVE_CONFIRM' : 'message.MEETING_END_CONFIRM'), [
        {
          title: this.$t(this.isMeetingSpectator ? 'message.MEETING_LEAVE' : 'message.MEETING_END'),
          action: () => {
            this.$store.dispatch(`meeting/${LEAVE_MEETING}`);
            globalEventDispatcher.dispatch(ShizenGlobalEvent.MEETING_ENDED);
          },
        },
        {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    sliderLabelClass(index) {
      const step = this.stepAtDuration[index];
      const isBottom = (step.number % 2 === 0);
      return {
        bubble: this.bubble,
        'vslider-bottom-label': isBottom,
        'vslider-top-label': !isBottom,
        clickable: !this.isMeetingSpectator,
        current: this.currentStepNb === (step.number - 1),
      };
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.meeting-navigator {
  position: fixed;
  z-index: 10005;
  left: calc(10% + #{$nav-width});
  bottom: 20px;
  width: calc(80% - #{$nav-width});
  transition: transform 0.3s ease-in;
  pointer-events: none;

  &__closed-buttons {
    display: flex;
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translate(-50%, 0);
  }

  &__step-viewer {
    z-index: -1;
    opacity: 0;
    transition: z-index 0s, opacity 0.3s ease;
    background: rgba(255, 255, 255, 0.5);
    position: absolute;
    left: 50%;
    bottom: 50px;
    transform: translate(-50%, 0);
    padding: 3px;
    border-radius: $main-border-radius;
  }

  &.closed {
    transform: translateY(100%) translateY(-30px);

    .header .close-img {
      transform: rotate(180deg);
      opacity: 0.7;

      &:hover {
        opacity: 1;
      }
    }
  }
  .header {
    position: relative;

    .close-img {
      border-bottom: none;
      margin: auto;
      z-index: 10003;
      position: relative;
      pointer-events: all;
      cursor: pointer;
      margin-bottom: 0.75em;

      &--icon {
        width: 1.5rem;
        height: 1.5rem;
      }

      @media (max-width: $smartphone-portrait) {
        width: 41px;
        height: 41px;
      }
    }

    .left-right__img {
      z-index: 1;
      opacity: 0.7;
      pointer-events: all;
      margin: 0 2.5em;

      @media (max-width: $smartphone-portrait) {
        opacity: 1;
      }

      &:hover {
        opacity: 1;
      }

      &:hover ~ .meeting-navigator__step-viewer {
        z-index: 1;
        opacity: 1;
      }
    }

  }
  > .content {
    pointer-events: initial;
    width: 100%;
    background: $color-background-grey;
    border: solid 1px $color-shizen-blue-border;
    border-radius: $main-border-radius;

    > .bottom-content {
      background: white;
      border-radius: $main-border-radius;
      position: relative;
      padding: 0.75em 0.5em;

      @media (max-width: $smartphone-portrait) {
        padding: 1em 0.5rem;
      }

      > div {
        vertical-align: middle;
      }
    }
  }

  .step-info {
    padding: 0.5rem;
  }

  .m-nav-buttons {
    width: 22%;
  }

  .m-nav-buttons > .img-wrapper {
    text-align: center;
    width: 25%;
    cursor: pointer;
  }

  .m-nav-buttons > span {
    width: 50%;
    height: 40px;
    position: relative;
    cursor: pointer;
  }

  .m-nav-buttons > span.highlight {
    font-weight: 600;
    color: rgb(42, 90, 145);
  }

  .m-nav-buttons > span > div {
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    line-height: 1.2em;
    height: 3.7em;
    text-align: center;
    font-size: 0.8em;
  }

  .m-nav-buttons .img-wrapper img {
    margin: 15%;
    width: 70%;
    display: block;
  }

  // Blue bubble to reach the current step of the meeting
  .m-nav-cstep-bubble {
    width: 16px;
    height: 16px;
    border-radius: $main-border-radius;
    padding: 9px 14px;
    background: rgb(42, 90, 145);
    position: absolute;
    top: -100%;
    left: 50px;
    box-shadow: 0px 0px 15px;
    cursor: pointer;
    transition: all 0.3s ease-in;
  }

  .m-nav-cstep-bubble img {
    height: auto;
  }

  .m-nav-cstep-bubble:after {
    border: solid 10px transparent;
    position: absolute;
    top: 85%;
    left: 50%;
    border-top: solid 20px rgb(42, 90, 145);
    content: ' ';
    height: 0;
    width: 0;
    margin-left: -10px;
  }

  &.closed .m-nav-cstep-bubble {
    top: -150%;
    left: calc(50% - 22.5px);
  }

  &.closed .m-nav-cstep-bubble:after {
    display: none;
  }

  // Main content
  .m-nav-slider {
    position: absolute;
    top: 0px;
    bottom: 0px;
    left: 30%;
    width: 65%;
    padding-left: 5%;
    margin: 0.5em 0;
    box-sizing: border-box;

    .vue-slider.vue-slider-disabled {
      opacity: 1;
      cursor: default;

      .vue-slider-mark-dot {
        background-color: transparent;
      }
    }
  }

  > div.m-nav-time {
    vertical-align: middle;
    width: 13%;
    padding-left: 10px;
  }

  .vslider-bottom-label, .vslider-top-label {
    font-size: 11px;
    display: inline-block;
    white-space: nowrap;
  }

  .vslider-bottom-label.clickable,
  .vslider-top-label.clickable {
    cursor: pointer;
  }

  .bubble {
    background: $color-gray-background;
    color: $color-shizen-blue;
    font-weight: 800;
    width: 17px;
    height: 17px;
    border-radius: $main-border-radius;
    position: absolute;

    &.current {
      background: $color-shizen-blue;
      color: $color-gray-background;
    }

    span {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &.vslider-bottom-label {
      transform: translate(-50%, 35%);
    }

    &.vslider-top-label {
      transform: translate(-50%, -110%);
    }
  }


  // Roles button to expand
  .role-displayer {
    cursor: pointer;
    pointer-events: auto;
    position: absolute;
    display: inline;
    right: 8px;
    bottom: -11px;
    width: 200px;
    z-index: 10005;

    .role-container {
      cursor: pointer;
      overflow: hidden;
      height: 0;
      background: rgba(255, 255, 255, 0.8);
      border-bottom: none;
      border-top: none;

      .role-definition {
        color: $color-shizen-blue;
        text-overflow: ellipsis;
        padding: 5px;

        .image-container {
          display: inline-block;
          vertical-align: middle;
          width: 30px;
          height: 30px;

          .role-image {
            color: white;
            width: 100%;
            height: 100%;
            padding: 5px;
            box-sizing: border-box;
          }
        }
      }
      .role-content {
        display: inline-block;
        vertical-align: middle;
        margin-left: 5px;
        max-width: calc(100% - 37px);
        max-height: 1.4em;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .no-roles {
        color: $color-shizen-blue;
        text-align: center;
        padding: 4px;
      }
    }
    .button-role {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      height: 25px;
      background: $color-shizen-blue;
      border-top-right-radius: $main-border-radius;
      border-top-left-radius: $main-border-radius;
      text-align: center;

      .button-element {
        display: inline-block;
        padding: 2px 0px;
      }
      .button-title {
        color: #fff;
      }
      .arrow {
        transition: transform 0.3s ease;
        color: white;
        transform: rotate(180deg);
        &.expand, .arrow {
          transform: rotate(0);
        }
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .meeting-navigator {
    left: calc(2% + #{$smartphone-nav-width});
    width: calc(96% - #{$smartphone-nav-width});
    bottom: 35px;

    &.closed {
      transform: translateY(100%) translateY(-10px);
    }
    .role-displayer {
      width: 100px;

      .button-role .button-element {
        padding: 4px 0px;
      }
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .meeting-navigator {
    .m-nav-buttons {
      display: none !important;
    }
  }
}
</style>
